import { NotificationRule } from '@eppendorf/vnls-notifications-utils';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';

import { fetcher } from '$shared/fetch';
import { PaginatedResponse, Pagination } from '$shared/types';
import {
  createPathWithQueryParams,
  NOTIFICATION_RULES_BASE_PATH,
  objectToURLSearchParams,
} from '$shared/utils/api-paths';

import {
  CreateNotificationRuleInput,
  UpdateNotificationRuleInput,
} from '$features/notifications/types';

export const notificationRulesQueryKey = 'notification-rules';
export const addedNotificationRuleQueryKey = 'added-notification-rule';

export const createNotificationRulesQueryWithQueryParams = (queryParams: Pagination) => ({
  queryKey: [notificationRulesQueryKey, ...objectToURLSearchParams(queryParams).values()],
  queryFn: () =>
    fetcher.get<PaginatedResponse<NotificationRule>>(
      createPathWithQueryParams(NOTIFICATION_RULES_BASE_PATH, queryParams),
    ),
  placeholderData: keepPreviousData,
});

export const createGetNotificationRuleByIdQuery = (id: string) => ({
  queryKey: [notificationRulesQueryKey, id],
  queryFn: () => fetcher.get<NotificationRule>(`${NOTIFICATION_RULES_BASE_PATH}/${id}`),
  enabled: !!id,
});

export const useGetNotificationRule = (id: string) =>
  useQuery(createGetNotificationRuleByIdQuery(id));

export const useNotificationRulesWithQueryParams = (queryParams: Pagination) =>
  useQuery(createNotificationRulesQueryWithQueryParams(queryParams));

export const useAddedNotificationRule = () =>
  useQuery<NotificationRule | null>({
    queryKey: [addedNotificationRuleQueryKey],
    queryFn: async () => null,
    staleTime: Infinity,
  });

export const useAddNotificationRule = () => {
  const addNotificationRule = (notificationRule: CreateNotificationRuleInput) =>
    fetcher.post<CreateNotificationRuleInput, NotificationRule>(
      NOTIFICATION_RULES_BASE_PATH,
      notificationRule,
    );

  const queryClient = useQueryClient();
  return useMutation<
    AxiosResponse<NotificationRule>,
    AxiosError,
    CreateNotificationRuleInput
  >({
    mutationFn: addNotificationRule,
    // This is sufficient for now. When we tackle performance we can utilise
    // optimistic updates or saving another single device request by mutating data instead
    onSuccess: ({ data }) => {
      queryClient.invalidateQueries({ queryKey: [notificationRulesQueryKey] });
      queryClient.setQueryData([notificationRulesQueryKey, data.id], data);
      queryClient.setQueryData([addedNotificationRuleQueryKey], data);
    },
  });
};

export const useUpdateNotificationRule = () => {
  const updateNotificationRule = ({ id, ...body }: UpdateNotificationRuleInput) =>
    fetcher.patch<Partial<CreateNotificationRuleInput>, NotificationRule>(
      `${NOTIFICATION_RULES_BASE_PATH}/${id}`,
      body,
    );

  const queryClient = useQueryClient();
  return useMutation<NotificationRule, AxiosError, UpdateNotificationRuleInput>({
    mutationFn: updateNotificationRule,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [notificationRulesQueryKey] });

      queryClient.setQueryData([notificationRulesQueryKey, variables.id], data);
    },
  });
};

export const useArchiveNotificationRule = () => {
  const archiveNotificationRule = (id: string) =>
    fetcher.patch<never, NotificationRule>(
      `${NOTIFICATION_RULES_BASE_PATH}/${id}/actions/archive`,
    );

  const queryClient = useQueryClient();
  return useMutation<NotificationRule, Error, string>({
    mutationFn: archiveNotificationRule,
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: [notificationRulesQueryKey] });
      queryClient.removeQueries({
        queryKey: [notificationRulesQueryKey, id],
        exact: true,
      });
    },
  });
};

export const usePauseNotificationRule = () => {
  const pauseNotificationRule = ({ id, paused }: { id: string; paused: boolean }) =>
    fetcher.patch<{ paused: boolean }, NotificationRule>(
      `${NOTIFICATION_RULES_BASE_PATH}/${id}/actions/pause`,
      { paused },
    );

  const queryClient = useQueryClient();
  return useMutation<NotificationRule, Error, { id: string; paused: boolean }>({
    mutationFn: pauseNotificationRule,
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({ queryKey: [notificationRulesQueryKey] });
      queryClient.setQueryData([notificationRulesQueryKey, id], data);
    },
  });
};
